import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import axios from 'axios'

export default function PremiumRoute({ children, user }) {
  const [appPremiumMode, setAppPremiumMode] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hasAccess, setHasAccess] = useState(null)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    // Fetch app premium mode status and refresh user data
    const fetchPremiumStatus = async () => {
      try {
        // Fetch fresh user data to get latest isPremium status
        // (in case it was changed by a Global Admin)
        const userResponse = await axios.get('/auth/profile')
        const freshUserData = userResponse.data.user
        const isUserPremium = freshUserData?.isPremium || user?.isPremium

        const response = await axios.get('/global-admin/public/premium-mode')
        const isAppInPremiumMode = response.data.appPremiumMode
        
        console.log('📊 Premium Status Check:', {
          appMode: isAppInPremiumMode ? 'Premium Mode' : 'Free Mode',
          userIsPremium: isUserPremium,
          hasAccess: !isAppInPremiumMode || isUserPremium
        })

        setAppPremiumMode(isAppInPremiumMode)
        
        // Access logic:
        // - If app is in Free Mode (appPremiumMode=false) -> all users allowed
        // - If app is in Premium Mode (appPremiumMode=true) -> only premium users allowed
        if (!isAppInPremiumMode) {
          // Free Mode - grant access to all
          setHasAccess(true)
        } else if (isUserPremium) {
          // Premium Mode and user is premium - grant access
          setHasAccess(true)
        } else {
          // Premium Mode and user is not premium - deny access
          setHasAccess(false)
        }
      } catch (error) {
        console.error('Error checking premium status:', error)
        // If fetch fails, assume Premium Mode and user must be premium
        setAppPremiumMode(true)
        setHasAccess(user?.isPremium || false)
      } finally {
        setLoading(false)
      }
    }

    fetchPremiumStatus()
  }, [user])

  // Not logged in - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Still loading premium status
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Access denied - show upgrade message
  if (hasAccess === false) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-5xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Premium Feature</h2>
            <p className="text-gray-600 mb-6">
              The User Admin Dashboard is a premium feature. Please upgrade to access this feature.
            </p>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Access granted - render children
  return children
}
